import { endpoints, baseurl } from "../Utils/Constants";

export const emailAnalyticsChatSummary = async (payload, token, endpoint = endpoints.ANALYTICS_CHAT_SUMMARY_EMAIL) => {
    try {
        const response = await fetch(baseurl + endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });
        return await response.json();
    } catch (error) {
        console.error("Error emailing analytics chat summary:", error);
        return { is_success: false, message: error.message || "Failed to email the conversation summary." };
    }
};

const parseSseEvent = (rawEvent) => {
    const lines = rawEvent.split(/\r?\n/);
    let eventType = "message";
    const dataLines = [];

    for (const line of lines) {
        if (line.startsWith("event:")) {
            eventType = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).replace(/^ /, ""));
        }
    }

    return { eventType, data: dataLines.join("\n") };
};

export const streamAnalyticsChat = async (payload, token, onChunk, onAck, onDone, onFollowups, onReset, onMeta, onThinking, endpoint = endpoints.ANALYTICS_CHAT) => {
    const handleEvent = (eventType, data) => {
        if (eventType === "ack") {
            if (typeof onAck === "function") onAck(data);
        } else if (eventType === "meta") {
            if (typeof onMeta !== "function") return;
            try {
                const parsed = JSON.parse(data);
                if (parsed && parsed.telemetry_id) onMeta(parsed.telemetry_id);
            } catch (err) {
                console.warn("Ignoring malformed meta event:", err);
            }
        } else if (eventType === "reset") {
            if (typeof onReset === "function") onReset();
        } else if (eventType === "done") {
            if (typeof onDone === "function") onDone();
        } else if (eventType === "followups") {
            if (typeof onFollowups !== "function") return;
            try {
                const parsed = JSON.parse(data);
                if (Array.isArray(parsed?.questions)) onFollowups(parsed.questions);
            } catch (err) {
                console.warn("Ignoring malformed followups event:", err);
            }
        } else if (eventType === "thinking") {
            if (typeof onThinking === "function") onThinking(data);
        } else if (typeof onChunk === "function") {
            onChunk(data);
        }
    };

    try {
        const response = await fetch(baseurl + endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "ngrok-skip-browser-warning": "true",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.body) {
            const text = await response.text();
            return {
                is_success: response.ok,
                message: text || (response.ok ? "" : "The analytics assistant is unavailable right now."),
            };
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let streamError = null;

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            let boundary = buffer.indexOf("\n\n");
            while (boundary !== -1) {
                const { eventType, data } = parseSseEvent(buffer.slice(0, boundary));
                buffer = buffer.slice(boundary + 2);

                if (eventType === "error") {
                    if (data) streamError = data;
                } else if (data || eventType === "reset") {
                    handleEvent(eventType, data);
                }
                boundary = buffer.indexOf("\n\n");
            }
        }

        if (buffer.trim()) {
            const { eventType, data } = parseSseEvent(buffer);
            if (eventType === "error") {
                if (data) streamError = data;
            } else if (data || eventType === "done" || eventType === "reset") {
                handleEvent(eventType, data);
            }
        }

        if (streamError) {
            return { is_success: false, message: streamError };
        }
        if (!response.ok) {
            return { is_success: false, message: "The analytics assistant is unavailable right now." };
        }
        return { is_success: true, message: "" };
    } catch (error) {
        console.error("Error streaming analytics chat:", error);
        return { is_success: false, message: error.message || "Failed to reach the analytics assistant." };
    }
};
